import React from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  MapPin, 
  Github, 
  LinkedinIcon, 
  BookOpen,
  ExternalLink,
  ArrowRight,
  LucideIcon
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../components/shared/Card';
import { Section, SectionHeader } from '../../components/shared/Section';
import { cn } from '../../lib/utils';
import { getThemeClass } from '../../lib/theme';
import { HeroRibbon } from '../Home/components/HeroSection';
import PageLayout from '../../layouts/PageLayout';
import StackOverflowIcon from '../../lib/StackOverflowIcon';

interface ContactMethod {
  icon: LucideIcon;
  title: string;
  content: string;
  href: string | null;
  description?: string;
}

interface ProfileLink {
  href: string;
  content: string;
  description: string;
}

interface ContactProfile {
  icon: LucideIcon;
  title: string;
  /** Single-link profile (StackOverflow, GitHub) */
  href?: string;
  content?: string;
  description?: string;
  /** Multi-link card */
  links?: ProfileLink[];
}

const ContactPage = () => {
  const contactMethods: ContactMethod[] = [
    {
      icon: Mail,
      title: 'Email',
      content: 'chintantank.work@gmail.com',
      href: 'mailto:chintantank.work@gmail.com',
      description: 'Best for professional inquiries and collaboration opportunities'
    },
    {
      icon: LinkedinIcon,
      title: 'LinkedIn',
      content: 'Connect on LinkedIn',
      href: 'https://www.linkedin.com/in/chintantank',
      description: 'Professional network and career updates'
    },
    {
      icon: MapPin,
      title: 'Location',
      content: 'San Francisco Bay Area',
      href: null,
      description: 'Available for local collaborations and meetups'
    }
  ];

  const profiles: ContactProfile[] = [
    {
      icon: StackOverflowIcon,
      title: 'StackOverflow',
      content: 'Developer Profile on StackOverflow',
      href: 'https://stackoverflow.com/users/131315/chantz',
      description: 'Technical problem-solving and community contributions'
    },
    {
      icon: Github,
      title: 'GitHub',
      content: 'Personal Contributions',
      href: 'https://github.com/tankchintan',
      description: 'Open source contributions and technical work'
    },
    {
      icon: BookOpen,
      title: 'Research',
      links: [
        {
          href: '/research',
          content: 'See full publications and presentations',
          description: 'Research papers, professional articles, patents, and talks'
        }
      ]
    }
  ];

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroRibbon
        title="Connect with a Technical Leader"
        subtitle="Chintan is open to research collaboration, conference and journal reviewing, practitioner writing, and technical conversations about edge computing and AI platforms. Use the channels below to get in touch."
      />

      {/* Contact Methods Section */}
      <Section background="light" spacing="sm" withDivider={true}>
        <SectionHeader
          title="Contact Information"
          subtitle="Direct ways to reach out and connect"
          align="left"
        />
        
        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10"
        >
          {contactMethods.map((method, index) => (
              <Card
                key={`contact-index-${index}`}
                variant="default"
                size="lg"
                className={cn(
                  "h-full group transition-all duration-300",
                  "hover:shadow-lg",
                  getThemeClass('colors.border.hover', 'borderHover')
                )}
              >
                <CardHeader
                  icon={method.icon}
                  title={method.title}
                  iconClassName={cn(
                    "w-6 h-6",
                    "transition-colors duration-300",
                    getThemeClass('colors.primary.main'),
                    "group-hover:text-sky-500"
                  )}
                />
                <CardContent>
                  <div className="space-y-2">
                    {method.href ? (
                      <a
                        href={method.href}
                        target={method.href.startsWith('mailto') ? undefined : '_blank'}
                        rel="noopener noreferrer"
                        className={cn(
                          "group/link flex items-center",
                          "transition-colors duration-300",
                          getThemeClass('colors.primary.main'),
                          "hover:text-sky-600"
                        )}
                      >
                        {method.content}
                        <ExternalLink className={cn(
                          "ml-2 w-4 h-4",
                          "transition-transform duration-300",
                          "group-hover/link:translate-x-1"
                        )} />
                      </a>
                    ) : (
                      <span className={getThemeClass('colors.text.secondary')}>
                        {method.content}
                      </span>
                    )}
                    {method.description && (
                      <p className={cn(
                        "text-sm",
                        getThemeClass('colors.text.secondary')
                      )}>
                        {method.description}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
          ))}
        </motion.div>
      </Section>

      {/* Professional Profiles Section */}
      <Section
        background="light"
        spacing="sm"
        className={cn(
          getThemeClass('colors.background.light'),
          'relative'
        )}
        withDivider={true}
      >
          <SectionHeader
            title="Professional Profiles"
            subtitle="Connect through professional networks and explore contributions"
            align="left"
          />
          
          <motion.div
            variants={containerAnimation}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10"
          >
            {profiles.map((profile, index) => (
              <motion.div
                key={index}
                variants={itemAnimation}
                className="h-full"
              >
                <Card
                  variant="bordered"
                  size="lg"
                  className={cn(
                    "h-full group transition-all duration-300",
                    "hover:shadow-lg",
                    getThemeClass('colors.border.hover', 'borderHover')
                  )}
                >
                  <CardHeader
                    icon={profile.icon}
                    title={profile.title}
                    iconClassName={cn(
                      "w-6 h-6",
                      "transition-colors duration-300",
                      getThemeClass('colors.secondary.main'),
                      "group-hover:text-indigo-500"
                    )}
                  />
                  <CardContent>
                    <div className="space-y-3">
                      {profile.links ? (
                        profile.links.map((link, linkIdx) => (
                          <div key={linkIdx} className="space-y-1">
                            <a
                              href={link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cn(
                                "block group/link",
                                "transition-colors duration-300",
                                getThemeClass('colors.secondary.main'),
                                "hover:text-indigo-600"
                              )}
                            >
                              <span className="flex items-center">
                                {link.content}
                                <ArrowRight className={cn(
                                  "ml-2 w-4 h-4",
                                  "transition-transform duration-300",
                                  "group-hover/link:translate-x-1"
                                )} />
                              </span>
                            </a>
                            <p className={cn(
                              "text-sm",
                              getThemeClass('colors.text.secondary')
                            )}>
                              {link.description}
                            </p>
                          </div>
                        ))
                      ) : (
                        <>
                          <a
                            href={profile.href || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cn(
                              "block group/link",
                              "transition-colors duration-300",
                              getThemeClass('colors.secondary.main'),
                              "hover:text-indigo-600"
                            )}
                          >
                            <span className="flex items-center">
                              {profile.content}
                              <ArrowRight className={cn(
                                "ml-2 w-4 h-4",
                                "transition-transform duration-300",
                                "group-hover/link:translate-x-1"
                              )} />
                            </span>
                          </a>
                          {profile.description && (
                            <p className={cn(
                              "text-sm",
                              getThemeClass('colors.text.secondary')
                            )}>
                              {profile.description}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
      </Section>

      {/* Call to Action */}
      <Section background="light" spacing="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={cn(
            "text-center p-12 rounded-lg",
            "border",
            getThemeClass('colors.border.light'),
            getThemeClass('colors.background.surface')
          )}
        >
          <h3 className={cn(
            "text-2xl font-bold mb-4",
            getThemeClass('colors.text.primary')
          )}>
            Ready to Collaborate?
          </h3>
          <p className={cn(
            "text-lg mb-8 max-w-2xl mx-auto",
            getThemeClass('colors.text.secondary')
          )}>
            Whether you're interested in technical innovation, research opportunities, 
            or professional collaboration, I'm always open to connecting with 
            like-minded professionals.
          </p>
          <a
            href="mailto:chintantank.work@gmail.com"
            className={cn(
              "inline-flex items-center",
              "px-6 py-3 rounded-lg",
              "text-white font-medium",
              "transition-all duration-300",
              "hover:shadow-lg",
              getThemeClass('colors.primary.surface'),
              getThemeClass('colors.primary.main')
            )}
          >
            Send an Email
            <Mail className="ml-2 w-5 h-5" />
          </a>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default ContactPage;
